import { Decoration, DecorationSet } from '@milkdown/kit/prose/view';
import { DOMSerializer, Fragment } from '@milkdown/kit/prose/model';
import type { Schema, Node as ProseMirrorNode } from '@milkdown/kit/prose/model';
import type { EditorView } from '@milkdown/kit/prose/view';
import removeMarkdown from '@brainfish-ai/remove-markdown';

import type { ResolvedReviewSuggestionRange } from './types';

export type ParseSuggestionMarkdown = (markdown: string) => ProseMirrorNode | null | undefined;
export type ParseProposedMd = ParseSuggestionMarkdown;

const baseClass = 'review-suggestion';

const buildClassList = (variantClass: string, isSelected: boolean) => {
  const classList = [baseClass, variantClass];
  if (isSelected) classList.push('selected');

  return classList.join(' ');
};

const targetDeleteClass = (isSelected: boolean) => buildClassList('target-delete', isSelected);
const targetReplaceHiddenClass = buildClassList('target-replace-hidden', false);
const inlineAddClass = (isSelected: boolean) => buildClassList('inline-add', isSelected);
const blockAddClass = (isSelected: boolean) => buildClassList('block-add', isSelected);

const reviewWidgetKey = (id: string, isSelected: boolean) => `review-widget-${id}-${isSelected ? 'selected' : 'unselected'}`;
const attachmentRedirectPath = '/api/attachments.redirect';

function isAttachmentRedirectUrl(url: string | null): boolean {
  return Boolean(url?.toLowerCase().includes(attachmentRedirectPath));
}

export function looksLikeBlockSuggestion(markdown: string): boolean {
  const trimmed = markdown.trim();
  if (!trimmed) return false;
  if (/^\s*\n/.test(markdown) || /\n\s*\n/.test(markdown)) return true;
  if (/^\s{0,3}(?:#{1,6}\s+|(?:[-*+]|\d+[.)])\s+)/.test(markdown)) return true;
  if (/^\s*::\w+[{(]/.test(markdown)) return true;
  if (/^\s*\[[^\]]*(?:\d+x\d+)?\]\([^)]*\.(?:mp4|mov|avi|wmv|flv|webm|mkv|m4v)(?:[?#][^)]*)?\)\s*$/i.test(trimmed)) return true;
  if (/^\s*\[[^\]]*https?:\/\/(?:www\.)?(?:youtube(?:-nocookie)?\.com|youtu\.be|vimeo\.com)[^\]]*\]\([^)]*\)\s*$/i.test(trimmed))
    return true;
  if (isAttachmentRedirectUrl(extractSingleMarkdownUrl(trimmed))) return true;

  return false;
}

/**
 * Unwrap parsed doc to the Fragment used for inline chips and apply/replace slices:
 * doc→single paragraph, or doc→blockquote→paragraph.
 */
export function unwrapParsedInline(parsed: ProseMirrorNode): Fragment {
  const { content } = parsed;
  if (content.childCount === 1) {
    const only = content.firstChild;
    if (only?.type.name === 'paragraph') {
      return only.content;
    }
    if (only?.type.name === 'blockquote' && only.childCount === 1) {
      const inner = only.firstChild;
      if (inner?.type.name === 'paragraph') {
        return inner.content;
      }
    }
  }

  return content;
}

function fragmentFullPlainText(fragment: Fragment): string {
  let s = '';
  fragment.forEach((node) => {
    if (node.isText) s += node.text ?? '';
    else if (node.content.size) s += fragmentFullPlainText(node.content);
  });

  return s;
}

function fragmentChildNodes(fragment: Fragment): ProseMirrorNode[] {
  const out: ProseMirrorNode[] = [];
  fragment.forEach((node) => {
    out.push(node);
  });

  return out;
}

export function isFragmentInlineOnly(fragment: Fragment): boolean {
  for (let i = 0; i < fragment.childCount; i += 1) {
    if (fragment.child(i).isBlock) return false;
  }

  return true;
}

/**
 * CommonMark / Milkdown often drop leading or trailing **horizontal** whitespace inside the parsed
 * paragraph; API `proposedText` still carries it for insert/replace UX (e.g. a space
 * after "1965." or before "(double album)"). Only spaces/tabs are restored at the edges;
 * newlines are not injected as text nodes (would break inline PM structure).
 */
export function inlineFragWithEdgeWs(schema: Schema, parsed: ProseMirrorNode, rawMarkdown: string): Fragment {
  const inner = unwrapParsedInline(parsed);
  const full = fragmentFullPlainText(inner);
  const lead = rawMarkdown.match(/^[ \t]+/)?.[0] ?? '';
  const trail = rawMarkdown.match(/[ \t]+$/)?.[0] ?? '';
  const prefixOk = !lead || full.startsWith(lead);
  const suffixOk = !trail || full.endsWith(trail);

  if (prefixOk && suffixOk) return inner;

  const nodes: ProseMirrorNode[] = [...fragmentChildNodes(inner)];
  if (!prefixOk) nodes.unshift(schema.text(lead));
  if (!suffixOk) nodes.push(schema.text(trail));

  return Fragment.from(nodes);
}

function fillChipFromMd(el: HTMLElement, replacementMarkdown: string, view: EditorView, parseMarkdown: ParseSuggestionMarkdown): boolean {
  if (!replacementMarkdown) return true;
  try {
    const parsed = parseMarkdown(replacementMarkdown);
    if (parsed) {
      const serializer = DOMSerializer.fromSchema(view.state.doc.type.schema);
      const doc = view.dom.ownerDocument ?? document;
      serializer.serializeFragment(inlineFragWithEdgeWs(view.state.schema, parsed, replacementMarkdown), { document: doc }, el);

      return el.childNodes.length > 0;
    }
  } catch {
    // fall through to plain text
  }

  return false;
}

function fillBlockChipFromMd(
  el: HTMLElement,
  replacementMarkdown: string,
  view: EditorView,
  parseMarkdown: ParseSuggestionMarkdown,
): boolean {
  if (!replacementMarkdown) return true;
  if (fillListPreviewFromMarkdown(el, replacementMarkdown)) return true;
  try {
    const parsed = parseMarkdown(replacementMarkdown);
    if (parsed) {
      const serializer = DOMSerializer.fromSchema(view.state.doc.type.schema);
      const doc = view.dom.ownerDocument ?? document;
      serializer.serializeFragment(parsed.content, { document: doc }, el);
      addMediaPreviewLabels(el, replacementMarkdown);

      return el.childNodes.length > 0;
    }
  } catch {
    // fall through to plain text
  }

  return false;
}

function listPreviewLines(markdown: string): string[] | null {
  const lines = markdown
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;

  const out: string[] = [];
  for (const line of lines) {
    const match = line.match(/^((?:[-*+])|(?:\d+[.)]))\s+(.+)$/);
    if (!match) return null;
    out.push(`${match[1]} ${removeMarkdown(match[2])}`);
  }

  return out;
}

function fillListPreviewFromMarkdown(el: HTMLElement, markdown: string): boolean {
  const lines = listPreviewLines(markdown);
  if (!lines) return false;

  const doc = el.ownerDocument ?? document;
  lines.forEach((line) => {
    const row = doc.createElement('div');
    row.className = 'list-preview-line';
    row.textContent = line;
    el.appendChild(row);
  });

  return true;
}

function extractSingleMarkdownUrl(markdown: string): string | null {
  const markdownLinkUrls = Array.from(markdown.matchAll(/\]\(([^)]+)\)/g), (match) => match[1]).filter(Boolean);
  const urls =
    markdownLinkUrls.length > 0
      ? markdownLinkUrls
      : Array.from(markdown.matchAll(/https?:\/\/\S+/g), (match) => match[0].replace(/[),.]+$/, ''));
  const uniqueUrls = [...new Set(urls)];

  return uniqueUrls.length === 1 ? uniqueUrls[0] : null;
}

function mediaLabel(type: string | null | undefined, url: string | null): string {
  const source = `${type ?? ''} ${url ?? ''}`.toLowerCase();
  if (source.includes('youtu')) return 'YouTube video';
  if (source.includes('vimeo')) return 'Vimeo video';
  if (url?.match(/\.(?:mp4|mov|avi|wmv|flv|webm|mkv|m4v)(?:[?#].*)?$/i)) return 'Video';

  return 'Embedded media';
}

function isMediaSuggestionUrl(url: string | null): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();

  return (
    lower.includes('youtube.com') ||
    lower.includes('youtube-nocookie.com') ||
    lower.includes('youtu.be') ||
    lower.includes('vimeo.com') ||
    isAttachmentRedirectUrl(url) ||
    /\.(?:mp4|mov|avi|wmv|flv|webm|mkv|m4v)(?:[?#].*)?$/i.test(url)
  );
}

function addMediaPreviewLabel(container: HTMLElement, label: string, url: string | null): void {
  if (container.querySelector('.review-suggestion.media-preview')) return;

  const doc = container.ownerDocument ?? document;
  const preview = doc.createElement('div');
  preview.className = 'review-suggestion media-preview';
  preview.textContent = url ? `${label}: ${url}` : label;
  container.appendChild(preview);
}

function addMediaPreviewLabels(el: HTMLElement, markdown: string): void {
  const fallbackUrl = extractSingleMarkdownUrl(markdown);
  el.querySelectorAll<HTMLElement>('.embed-block').forEach((embed) => {
    const url = embed.dataset.href || embed.dataset.src || fallbackUrl;
    addMediaPreviewLabel(embed, mediaLabel(embed.dataset.embedType, url), url);
  });
  el.querySelectorAll<HTMLElement>('.video-block').forEach((video) => {
    const url = video.dataset.src || fallbackUrl;
    addMediaPreviewLabel(video, mediaLabel('video', url), url);
  });

  if (
    !el.querySelector('.embed-block, .video-block') &&
    fallbackUrl &&
    isMediaSuggestionUrl(fallbackUrl) &&
    looksLikeBlockSuggestion(markdown)
  ) {
    const doc = el.ownerDocument ?? document;
    const embed = doc.createElement('div');
    embed.className = 'embed-block';
    embed.dataset.href = fallbackUrl;
    embed.dataset.embedType = mediaLabel(null, fallbackUrl).toLowerCase().replace(/\s+/g, '-');
    addMediaPreviewLabel(embed, mediaLabel(null, fallbackUrl), fallbackUrl);
    el.appendChild(embed);
  }
}

export function disableSuggestionChipInteractions(el: HTMLElement): void {
  el.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
    anchor.tabIndex = -1;
    anchor.setAttribute('aria-disabled', 'true');
    anchor.style.pointerEvents = 'none';
  });

  el.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
    video.removeAttribute('controls');
    video.style.pointerEvents = 'none';
  });

  el.querySelectorAll<HTMLIFrameElement>('iframe').forEach((iframe) => {
    iframe.tabIndex = -1;
    iframe.setAttribute('aria-disabled', 'true');
    iframe.style.pointerEvents = 'none';
  });
}

/**
 * Inline suggestion chip (insert/replace proposed text). Without `options`, renders
 * stripped plain text in a `<span>`. With `options`, parses markdown via Milkdown
 * and serializes into the chip (typically a `<div>` so block content is valid).
 */
export function createInlineSuggestionElement(
  suggestionId: string,
  replacementMarkdown: string,
  isSelected: boolean,
  options?: { view: EditorView; parseMarkdown: ParseSuggestionMarkdown },
) {
  if (options?.parseMarkdown && options.view) {
    const dom = (options.view.dom.ownerDocument ?? document).createElement('div');
    dom.className = inlineAddClass(isSelected);
    dom.dataset.reviewSuggestionId = suggestionId;
    dom.style.display = 'inline';
    if (fillChipFromMd(dom, replacementMarkdown, options.view, options.parseMarkdown)) {
      disableSuggestionChipInteractions(dom);

      return dom;
    }
  }

  const span = document.createElement('span');
  span.className = inlineAddClass(isSelected);
  span.dataset.reviewSuggestionId = suggestionId;
  span.textContent = removeMarkdown(replacementMarkdown);

  return span;
}

export function createBlockSuggestionElement(
  suggestionId: string,
  replacementMarkdown: string,
  isSelected: boolean,
  options: { view: EditorView; parseMarkdown: ParseSuggestionMarkdown },
) {
  const dom = (options.view.dom.ownerDocument ?? document).createElement('div');
  dom.className = blockAddClass(isSelected);
  dom.dataset.reviewSuggestionId = suggestionId;
  if (fillBlockChipFromMd(dom, replacementMarkdown, options.view, options.parseMarkdown)) {
    disableSuggestionChipInteractions(dom);

    return dom;
  }

  addMediaPreviewLabels(dom, replacementMarkdown);
  if (dom.childNodes.length > 0) {
    disableSuggestionChipInteractions(dom);

    return dom;
  }

  dom.textContent = removeMarkdown(replacementMarkdown);

  return dom;
}

function canRenderFallbackBlockSuggestion(replacementMarkdown: string): boolean {
  return Boolean(listPreviewLines(replacementMarkdown) || isMediaSuggestionUrl(extractSingleMarkdownUrl(replacementMarkdown)));
}

function shouldRenderBlockSuggestion(
  replacementMarkdown: string,
  parseMarkdown?: ParseSuggestionMarkdown,
  allowFallbackBlock = false,
): boolean {
  if (!parseMarkdown || !looksLikeBlockSuggestion(replacementMarkdown)) return false;
  try {
    const parsed = parseMarkdown(replacementMarkdown);
    if (!parsed) return allowFallbackBlock && canRenderFallbackBlockSuggestion(replacementMarkdown);

    return !isFragmentInlineOnly(inlineFragWithEdgeWs(parsed.type.schema, parsed, replacementMarkdown));
  } catch {
    return allowFallbackBlock && canRenderFallbackBlockSuggestion(replacementMarkdown);
  }
}

function blockReplaceWidgetPosition(doc: ProseMirrorNode, pos: number): number {
  try {
    const $pos = doc.resolve(pos);
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
      const nodeName = $pos.node(depth).type.name;
      if (nodeName === 'ordered_list' || nodeName === 'bullet_list') return $pos.after(depth);
    }
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
      if ($pos.node(depth).isTextblock) return $pos.after(depth);
    }
  } catch {
    // Fall back to the original widget position.
  }

  return pos;
}

function splitRangeIntoTextFragments(doc: ProseMirrorNode, from: number, to: number): Array<{ from: number; to: number }> {
  const fragments: Array<{ from: number; to: number }> = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText || !node.text) return true;
    const start = Math.max(from, pos);
    const end = Math.min(to, pos + node.text.length);
    if (start < end) fragments.push({ from: start, to: end });

    return true;
  });

  return fragments;
}

function fragmentPositionAt(index: number, total: number): 'single' | 'first' | 'middle' | 'last' {
  if (total === 1) return 'single';
  if (index === 0) return 'first';
  if (index === total - 1) return 'last';

  return 'middle';
}

function pushFragmentInlineDecorations(
  decorations: Decoration[],
  doc: ProseMirrorNode,
  range: ResolvedReviewSuggestionRange,
  className: string,
): boolean {
  const fragments = splitRangeIntoTextFragments(doc, range.from, range.to);
  if (fragments.length === 0) return false;
  fragments.forEach((fragment, i) => {
    decorations.push(
      Decoration.inline(fragment.from, fragment.to, {
        class: className,
        'data-review-suggestion-id': range.id,
        'data-fragment-position': fragmentPositionAt(i, fragments.length),
        contentEditable: 'false',
      }),
    );
  });

  return true;
}

export function buildDecorationSetFromRanges(
  doc: ProseMirrorNode,
  ranges: ResolvedReviewSuggestionRange[],
  selectedId: string | null | undefined,
  parseMarkdown?: ParseSuggestionMarkdown,
): DecorationSet {
  const decorations: Decoration[] = [];

  for (const range of ranges) {
    const isSelected = range.id === selectedId;

    if (range.opType === 'insert') {
      const renderBlock = shouldRenderBlockSuggestion(range.replacementText, parseMarkdown);
      decorations.push(
        Decoration.widget(
          range.from,
          (view) =>
            renderBlock && parseMarkdown
              ? createBlockSuggestionElement(range.id, range.replacementText, isSelected, { view, parseMarkdown })
              : createInlineSuggestionElement(
                  range.id,
                  range.replacementText,
                  isSelected,
                  parseMarkdown ? { view, parseMarkdown } : undefined,
                ),
          {
            side: 1,
            key: reviewWidgetKey(range.id, isSelected),
          },
        ),
      );
      continue;
    }

    if (range.opType === 'replace') {
      const renderBlock = shouldRenderBlockSuggestion(range.replacementText, parseMarkdown, true);
      const pushed = pushFragmentInlineDecorations(decorations, doc, range, targetReplaceHiddenClass);
      if (!pushed) continue;
      decorations.push(
        Decoration.widget(
          renderBlock ? blockReplaceWidgetPosition(doc, range.to) : range.to,
          (view) =>
            renderBlock && parseMarkdown
              ? createBlockSuggestionElement(range.id, range.replacementText, isSelected, { view, parseMarkdown })
              : createInlineSuggestionElement(
                  range.id,
                  range.replacementText,
                  isSelected,
                  parseMarkdown ? { view, parseMarkdown } : undefined,
                ),
          {
            side: renderBlock ? 1 : -1,
            key: reviewWidgetKey(range.id, isSelected),
          },
        ),
      );
      continue;
    }

    pushFragmentInlineDecorations(decorations, doc, range, targetDeleteClass(isSelected));
  }

  return DecorationSet.create(doc, decorations);
}

export function expectedWidgetCountFromRanges(ranges: ResolvedReviewSuggestionRange[]): number {
  let count = 0;
  for (const range of ranges) {
    if (range.opType === 'insert') count += 1;
    else if (range.opType === 'replace') count += 1;
  }

  return count;
}

export function actualWidgetCountFromDecorations(decorations: DecorationSet): number {
  // Widgets are represented as zero-width decorations in this plugin.
  return decorations.find().filter((deco) => deco.from === deco.to).length;
}
